import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Button,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { useMutation } from '@apollo/client';
import { db } from '../config/firebase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { CLAIM_PREVIEW_PLAN } from '../graphql/mutations';

interface OutlineStep {
  title: string;
  order: number;
  estimatedDuration: string;
  automatable: boolean;
  automationHint: string | null;
}

interface PreviewOutline {
  title: string;
  estimatedTimeline: string;
  steps: OutlineStep[];
}

interface PreviewPlanDoc {
  prompt?: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  outline: PreviewOutline | null;
  errorMessage: string | null;
}

export default function PreviewResultPage() {
  useDocumentTitle('Process Preview — apply pub');

  const { previewPlanId } = useParams<{ previewPlanId: string }>();
  const navigate = useNavigate();
  const cardScrollRef = useRef<HTMLDivElement>(null);

  const [planDoc, setPlanDoc] = useState<PreviewPlanDoc | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [claimSent, setClaimSent] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);

  const [claimPreviewPlan, { loading: claiming }] = useMutation(CLAIM_PREVIEW_PLAN);

  useEffect(() => {
    if (!previewPlanId) return;
    const unsub = onSnapshot(doc(db, 'preview_plans', previewPlanId), (snap) => {
      if (!snap.exists()) return;
      setPlanDoc(snap.data() as PreviewPlanDoc);
    });
    return () => unsub();
  }, [previewPlanId]);

  const status = planDoc?.status ?? 'QUEUED';
  const outline = planDoc?.outline;
  const prompt = planDoc?.prompt || 'Process';
  const errorMessage = planDoc?.errorMessage;
  const isGenerating = status === 'QUEUED' || status === 'RUNNING';
  const showContent = status === 'SUCCEEDED' && outline && Array.isArray(outline.steps);
  const steps = showContent
    ? [...outline.steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];
  const automatableCount = steps.filter(s => s.automatable).length;

  const handleClaim = async () => {
    if (!email.trim() || !previewPlanId) return;
    setClaimError(null);
    try {
      const { data } = await claimPreviewPlan({
        variables: { previewPlanId, email: email.trim() },
      });
      const result = data?.claimPreviewPlan;
      if (result?.success) {
        setClaimSent(true);
        if (result.magicLinkUrl) setMagicLinkUrl(result.magicLinkUrl);
      } else {
        setClaimError(result?.message || 'Something went wrong.');
      }
    } catch (err) {
      setClaimError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  if (!previewPlanId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8, px: 2 }}>
        <Typography sx={{ color: '#9CA3AF', mb: 2 }}>No preview plan found.</Typography>
        <Button onClick={() => navigate('/preview/guide')}>Go back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4, px: 2, pb: 8 }}>
      {/* Back button */}
      <Box sx={{ width: '100%', maxWidth: 800, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/preview/guide')}
          sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 400 }}
        >
          New process
        </Button>
      </Box>

      {/* Loading state */}
      {isGenerating && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 800,
            border: '1px solid #E0D8D0',
            borderRadius: 2,
            p: 3,
            mb: 3,
            bgcolor: '#FFFFFF',
          }}
        >
          <Typography sx={{ fontSize: 13, color: '#9CA3AF', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Process
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1A1A1A', mb: 2 }}>
            {prompt}
          </Typography>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: '#F0ECE6',
              '& .MuiLinearProgress-bar': { bgcolor: '#10B981', borderRadius: 2 },
            }}
          />
          <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.5 }}>
            {status === 'QUEUED' ? 'Starting up...' : 'Generating process outline...'}
          </Typography>
        </Paper>
      )}

      {/* Error state */}
      {status === 'FAILED' && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 800, mb: 3 }}>
          {errorMessage || 'Generation failed. Please try again.'}
        </Alert>
      )}

      {/* Generated Outline */}
      {showContent && (
        <>
          {/* Header */}
          <Box sx={{ width: '100%', maxWidth: 800, mb: 3 }}>
            <Typography
              sx={{ fontSize: 26, fontWeight: 700, color: '#1A1A1A', mb: 1.5, lineHeight: 1.3 }}
            >
              {outline!.title || prompt}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                  {outline!.estimatedTimeline || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                  {automatableCount} of {steps.length} steps automatable
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Horizontal step cards */}
          <Box
            ref={cardScrollRef}
            sx={{
              width: '100%',
              maxWidth: 800,
              mb: 4,
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#D1D5DB', borderRadius: 2 },
            }}
          >
            {steps.map((step, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    width: 180,
                    minHeight: 120,
                    border: '1px solid',
                    borderColor: step.automatable ? '#BBF7D0' : '#E0D8D0',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: step.automatable ? '#F0FDF4' : '#FFFFFF',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 22, height: 22, borderRadius: '50%',
                        bgcolor: step.automatable ? '#10B981' : '#6B7280',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 600, flexShrink: 0,
                      }}
                    >
                      {step.order}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: '#9CA3AF' }}>
                      {step.estimatedDuration}
                    </Typography>
                    {step.automatable && (
                      <SmartToyOutlinedIcon sx={{ fontSize: 12, color: '#10B981', ml: 'auto' }} />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', mb: 'auto',
                    }}
                  >
                    {step.title}
                  </Typography>
                  {step.automatable && step.automationHint && (
                    <Typography sx={{ fontSize: 10, color: '#10B981', mt: 0.75 }}>
                      {step.automationHint}
                    </Typography>
                  )}
                </Paper>
                {idx < steps.length - 1 && (
                  <ArrowForwardIcon sx={{ fontSize: 14, color: '#D1D5DB', mx: 0.5, flexShrink: 0 }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Step list */}
          <Box sx={{ width: '100%', maxWidth: 800 }}>
            {steps.map((step, idx) => (
              <Box key={idx}>
                {idx > 0 && <Divider sx={{ borderColor: '#F0ECE6' }} />}
                <Box sx={{ py: 2, px: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: 20, color: step.automatable ? '#10B981' : '#D1D5DB', flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#9CA3AF', flexShrink: 0 }}>
                        {step.estimatedDuration}
                      </Typography>
                    </Box>
                    {step.automatable && step.automationHint && (
                      <Chip
                        icon={<SmartToyOutlinedIcon sx={{ fontSize: 14 }} />}
                        label={step.automationHint}
                        size="small"
                        variant="outlined"
                        sx={{
                          mt: 0.5, height: 22, fontSize: 11,
                          bgcolor: '#F0FDF4', color: '#10B981', borderColor: '#BBF7D0',
                          '& .MuiChip-icon': { color: '#10B981' },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: '#D1D5DB' }} />
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#D1D5DB' }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* CTA */}
          <Paper
            elevation={0}
            sx={{
              width: '100%', maxWidth: 800,
              border: '2px solid #10B981', borderRadius: 2, p: 3, mt: 3,
              bgcolor: '#F0FDF4', textAlign: 'center',
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 28, color: '#10B981', mb: 1 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
              Sign up to save and manage this process
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 2 }}>
              Track progress, assign steps, set deadlines, and automate {automatableCount} steps.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setClaimOpen(true)}
              sx={{
                bgcolor: '#10B981', textTransform: 'none', fontWeight: 600, px: 4,
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              Get Started Free
            </Button>
          </Paper>
        </>
      )}

      {/* Claim dialog */}
      <Dialog
        open={claimOpen}
        onClose={() => { if (!claiming) setClaimOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {claimSent ? (
          <>
            <DialogContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                {magicLinkUrl ? 'Your process is ready' : 'Check your email'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#6B7280' }}>
                We sent a magic link to <strong>{email}</strong>.
                Click it to sign in and access your process.
              </Typography>
              {magicLinkUrl && (
                <Button
                  variant="contained"
                  href={magicLinkUrl}
                  sx={{
                    mt: 2, bgcolor: '#10B981', textTransform: 'none', fontWeight: 600,
                    '&:hover': { bgcolor: '#059669' },
                  }}
                >
                  Open Magic Link (dev)
                </Button>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                onClick={() => setClaimOpen(false)}
                sx={{ textTransform: 'none', color: '#6B7280' }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 600, pb: 0.5 }}>
              Get started with your process
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: 13, color: '#6B7280', mb: 2 }}>
                Enter your email and we'll send you a magic link to save and manage this process.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                type="email"
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleClaim(); }}
                disabled={claiming}
                size="small"
              />
              {claimError && (
                <Alert severity="error" sx={{ mt: 1.5, fontSize: 13 }}>
                  {claimError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setClaimOpen(false)}
                disabled={claiming}
                sx={{ textTransform: 'none', color: '#6B7280' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClaim}
                disabled={claiming || !email.trim()}
                variant="contained"
                sx={{
                  bgcolor: '#10B981', textTransform: 'none', fontWeight: 600,
                  '&:hover': { bgcolor: '#059669' },
                }}
              >
                {claiming ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Send Magic Link'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
